import { useEffect, useContext, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Col, Row } from "reactstrap";
import { GoDotFill } from "react-icons/go";
import {
  _imagesURL,
  ReadNotificationsApi,
  getNotificationsApi,
} from "../../constants/API";
import Avatar from "../../assets/images/icon/placeholder_Awatar.png";
import { toast } from "react-toastify";
import {
  EVENT_DETAILS,
  NEW_COURSE_DETAILS,
  POST_DETAILS,
  PROFILE,
} from "../../constants/RoutesUrl";
import { Language } from "../../App";
import ImageComponent from "../common/ImageComponent";
import { CountContext } from "../../constants/ProtectedRoute";
import { authorize } from "../../shared/authorize";
import { getSplashData } from "../../services/api";
import { useQuery } from "react-query";
import { GetNotificationBody } from "../common/GetNotificationBody";
import NotificationSkeleton from "./NotificationSkeletonWave";
import PurchasedUsersModal from "../course-details/PurchasedUsersModal";

const Notification = () => {
  const { count } = useContext(CountContext);
  const savedNotificationType = sessionStorage.getItem("lastNotificationType");
  const returnFromNavigation = sessionStorage.getItem("returnFromNotificationNavigation");

  const { lang } = useContext(Language);
  const { counts = {} } = count ?? {};

  const btns = [
    { id: 1, type: "social", name: lang?.posts, count: counts?.social },
    { id: 2, type: "webinar", name: lang?.webinars, count: counts?.webinar },
    { id: 3, type: "clinical", name: lang?.clinical_studies, count: counts?.clinical },
    { id: 4, type: "course", name: lang?.courses, count: counts?.course },
    { id: 5, type: "event", name: lang?.events, count: counts?.event },
    { id: 6, type: "clinical-session", name: lang?.clinical_sessions, count: counts?.clinical_session },
    { id: 7, type: "Wallet", name: lang?.wallet, count: counts?.wallet },
  ];

  const getActiveBtnFromType = (type) => {
    const btn = btns.find(btn => btn.type === type);
    return btn ? btn.id : 1;
  };

  const getInitialState = () => {
    if (returnFromNavigation === "true" && savedNotificationType) {
      return {
        activeBtn: getActiveBtnFromType(savedNotificationType),
        notificationsType: savedNotificationType
      };
    } else {
      return {
        activeBtn: 1,
        notificationsType: "social"
      };
    }
  };

  const initialState = getInitialState();

  const [dataLoading, setDataLoading] = useState(false);
  const [activeBtn, setActiveBtn] = useState(initialState.activeBtn);
  const [notificationsType, setNotificationsType] = useState(initialState.notificationsType);
  const [notificationData, setNotificationData] = useState({});
  const [isInitialized, setIsInitialized] = useState(false);
  const { refetch } = useQuery("splashData", getSplashData);

  useEffect(() => {
    if (returnFromNavigation === "true" && savedNotificationType) {
      const restoredActiveBtn = getActiveBtnFromType(savedNotificationType);
      setNotificationsType(savedNotificationType);
      setActiveBtn(restoredActiveBtn);
      sessionStorage.removeItem("returnFromNotificationNavigation");
    } else {
      setNotificationsType("social");
      setActiveBtn(1);
      sessionStorage.removeItem("lastNotificationType");
    }
    setIsInitialized(true);
  }, []);

  const handleReadNotification = () => {
    axios
      .post(ReadNotificationsApi, { type: notificationsType }, authorize())
      .then(() => refetch())
      .catch((err) => toast.error(err.message || "Failed to mark notifications as read"));
  };

  const handleGetNotifications = () => {
    setDataLoading(true);
    axios
      .post(getNotificationsApi, { type: notificationsType }, authorize())
      .then((res) => {
        if (res.data.status) {
          setNotificationData(res.data.data);
          handleReadNotification();
        } else {
          toast.error(res.data.action);
        }
        setDataLoading(false);
      })
      .catch((err) => {
        setDataLoading(false);
        toast.error(err);
      });
  };

  useEffect(() => {
    if (isInitialized) {
      handleGetNotifications();
    }
  }, [notificationsType, isInitialized]);

  const handleTabChange = (btnId, type) => {
    setActiveBtn(btnId);
    setNotificationsType(type);
    sessionStorage.setItem("lastNotificationType", type);
  };

  if (!isInitialized) {
    return (
      <Row className="justify-content-center">
        <Col lg={6} className="pb-4 custom-pt">
          <div className="bg-white rounded mb-5 p-3 border border-1 border-inherit">
            <div className="text-center pt-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <NotificationSkeleton key={index} notificationsType="social" />
              ))}
            </div>
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row className="justify-content-center">
      <Col lg={6} className="pb-4 custom-pt">
        <div className="bg-white rounded mb-5 p-3 border border-1 border-inherit">
          <div className="scroll-hide d-flex gap-2 mb-3 overflowX-auto">
            {btns.map((value) => (
              <div key={value.id}>
                <button
                  type="button"
                  className={`rounded px-5 py-2 fw-bold position-relative manue-hover hover-blue notification-tab-btn ${activeBtn === value.id ? "bg-dark text-white border-0" : "border border-1 border-inherit"}`}
                  onClick={() => handleTabChange(value.id, value.type)}
                >
                  {value.name}{" "}
                  {value.count !== 0 && value.count && (
                    <span className="notification-count-badge">
                      <div className="notification-count-circle">
                        <p className="notification-count-text">{value.count}</p>
                      </div>
                    </span>
                  )}
                </button>
              </div>
            ))}
          </div>

          {dataLoading ? (
            <div className="text-center pt-3">
              {Array.from({ length: 5 }).map((_, index) => (
                <NotificationSkeleton key={index} notificationsType={notificationsType} />
              ))}
            </div>
          ) : (
            <>
              {notificationData.data?.map((value) => (
                value.data.notification_type !== "follow" ? (
                  <NotificationCardLike key={value.id} value={value} currentNotificationType={notificationsType} />
                ) : (
                  <NotificationCardFollow key={value.id} value={value} currentNotificationType={notificationsType} />
                )
              ))}
            </>
          )}
        </div>
      </Col>
    </Row>
  );
};

export function UnixToRelative({ unixTimestamp }) {
  const currentTimestamp = Math.floor(Date.now() / 1000);
  const timestampDifference = currentTimestamp - unixTimestamp;
  const { lang } = useContext(Language);
  const intervals = [
    { label: lang?.year, seconds: 31536000 },
    { label: lang?.month, seconds: 2592000 },
    { label: lang?.day, seconds: 86400 },
    { label: lang?.hour, seconds: 3600 },
    { label: lang?.minute, seconds: 60 },
  ];

  for (let i = 0; i < intervals.length; i++) {
    const interval = intervals[i];
    const intervalCount = Math.floor(timestampDifference / interval.seconds);
    if (intervalCount >= 1) {
      if (intervalCount === 1) {
        return navigator.language.startsWith("en") ? `${intervalCount} ${interval.label} ${lang?.ago}` : `${lang?.ago} ${intervalCount} ${interval.label}`;
      } else {
        return navigator.language.startsWith("en") ? `${intervalCount} ${interval.label}s ${lang?.ago}` : `${lang?.ago} ${intervalCount} ${interval.label}s`;
      }
    }
  }

  return `${lang?.just_now}`;
}

const NotificationCardFollow = ({ value, currentNotificationType }) => {
  const navigate = useNavigate();

  const handleNavigation = (path) => {
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    navigate(path);
  };

  return (
    <div
      className="border border-1 border-inherit mb-3 rounded overflow-hidden cursor"
      onClick={() => handleNavigation(PROFILE + "/" + value.users[0].id)}
      key={value.id}
    >
      <div className="border-bottom border-inherit border-1">
        <h6 className="mx-3 my-2 pb-0">
          <GoDotFill color="#00c5de" /> {value.date}
        </h6>
      </div>
      <div className="px-3 py-3 manue-hover">
        <div className="d-flex gap-3">
          {value.users_count > 1 ? (
            <div className="d-flex">
              <div className="notification-avatar-wrapper">
                <ImageComponent
                  src={value?.users[0]?.thumbnail ? _imagesURL + value.users[0].thumbnail : Avatar}
                  compClassName="rounded-1 img-fluid"
                  loader_height="100%"
                  loader_width="100%"
                />
              </div>
              <div className="notification-avatar-second">
                <div className="notification-avatar-wrapper">
                  <ImageComponent
                    src={value?.users[1]?.thumbnail ? _imagesURL + value.users[1].thumbnail : Avatar}
                    compClassName="rounded-1 img-fluid"
                    loader_height="100%"
                    loader_width="100%"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="notification-avatar-wrapper">
              <ImageComponent
                src={value?.users[0]?.thumbnail ? _imagesURL + value.users[0].thumbnail : Avatar}
                compClassName="rounded-1 img-fluid"
                loader_height="100%"
                loader_width="100%"
              />
            </div>
          )}
          <div className="align-self-center">
            <div className="d-flex">
              {value.users_count > 1 ? (
                <p className="mb-0 fs-md-15">
                  <span className="fw-bold">{value?.users[0].name}</span> and{" "}
                  <span className="fw-bold">
                    {value.users_count > 2 ? "others" : value?.users[1].name}
                  </span>{" "}
                  {<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                </p>
              ) : (
                <p className="mb-0 fs-md-15">
                  <span className="fw-bold">{value?.users[0].name}</span>{" "}
                  {<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                </p>
              )}
            </div>
            <div>
              <small className="text-secondary">
                <UnixToRelative unixTimestamp={value.data.time} />
              </small>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const NotificationCardLike = ({ value, currentNotificationType }) => {
  const { lang } = useContext(Language);
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);

  const handleNavigation = (path) => {
    sessionStorage.setItem("lastNotificationType", currentNotificationType);
    sessionStorage.setItem("returnFromNotificationNavigation", "true");
    navigate(path);
  };

  const handleProfileNavigation = (userId) => {
    handleNavigation(PROFILE + "/" + userId);
  };

  const handleMediaNavigation = () => {
    let path;
    if (value.data.type === "event") {
      path = EVENT_DETAILS + value.data.post_id;
    } else if (value.data.type === "course") {
      sessionStorage.setItem("CourseId", value.data.post_id);
      path = NEW_COURSE_DETAILS + value.data.post_id;
    } else {
      path = POST_DETAILS + value.data.post_id;
    }
    handleNavigation(path);
  };

  return (
    <>
      <div className="border border-1 border-inherit mb-3 rounded overflow-hidden" key={value.id}>
        <div className="border-bottom border-inherit border-1">
          <h6 className="mx-3 my-2 pb-0">
            <GoDotFill color="#00c5de" /> {value.date}
          </h6>
        </div>
        <div className="px-3 py-3 manue-hover">
          <div className="d-flex justify-content-between">
            <div>
              <div className="d-flex gap-3">
                {value.users_count > 1 ? (
                  <div className="d-flex">
                    <div className="notification-avatar-wrapper">
                      <ImageComponent
                        src={value?.users[0]?.thumbnail ? _imagesURL + value.users[0].thumbnail : Avatar}
                        compClassName="rounded-1 img-fluid"
                        loader_height="100%"
                        loader_width="100%"
                      />
                    </div>
                    <div className="notification-avatar-second">
                      <div className="notification-avatar-wrapper">
                        <ImageComponent
                          src={value?.users[1]?.thumbnail ? _imagesURL + value.users[1].thumbnail : Avatar}
                          compClassName="rounded-1 img-fluid"
                          loader_height="100%"
                          loader_width="100%"
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="notification-avatar-wrapper cursor" onClick={() => handleProfileNavigation(value.users[0].id)}>
                    <ImageComponent
                      src={value?.users[0]?.thumbnail ? _imagesURL + value.users[0].thumbnail : Avatar}
                      compClassName="rounded-1 img-fluid"
                      loader_height="100%"
                      loader_width="100%"
                    />
                  </div>
                )}
                <div className="align-self-center">
                  <div className="d-flex">
                    {value.users_count > 1 ? (
                      <p className="mb-0 fs-md-15">
                        <span className="fw-bold cursor-pointer" onClick={() => handleProfileNavigation(value.users[0].id)}>
                          {value?.users[0].name}
                        </span> {lang.and}{" "}
                        <span className="fw-bold cursor-pointer" onClick={() => {
                          if (value.users_count > 2) setIsOpen(true);
                          else handleProfileNavigation(value.users[1].id);
                        }}>
                          {value.users_count > 2 ? lang.others : value?.users[1].name}
                        </span>{" "}
                        {<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                      </p>
                    ) : (
                      <p className="mb-0 fs-md-15">
                        <span className="fw-bold cursor-pointer" onClick={() => handleProfileNavigation(value.users[0].id)}>
                          {value?.users[0].name}
                        </span>{" "}
                        {<GetNotificationBody notificationType={value.data.notification_type} type={value.data.type} />}
                      </p>
                    )}
                  </div>
                  <div>
                    <small className="text-secondary">
                      <UnixToRelative unixTimestamp={value.data.time} />
                    </small>
                  </div>
                </div>
              </div>
            </div>
            {value.data.media && (
              <div className="notification-media-wrapper cursor" onClick={handleMediaNavigation}>
                <img
                  src={_imagesURL + value.data.media}
                  className="notification-media-img"
                  alt="picture"
                />
              </div>
            )}
          </div>
        </div>
      </div>
      <PurchasedUsersModal isOpen={isOpen} toggle={() => setIsOpen(!isOpen)} courseId={value.data.post_id} />
    </>
  );
};

export default Notification;